/*
  One set: its cards, filtered by grade and by name (§7.3, D18).

  Both controls on this page were dead. SetView held `search` and `filterGrade` in two
  useStates and SetViewTable destructured both and never read either — a search box and a
  grade dropdown that did nothing at all. They are wired now:

    - the search box is the §6.5 table filter, and it lives INSIDE the table state, so
      changing it always resets the page to 0 and §6.5's blank-page bug cannot be
      reintroduced by forgetting.
    - the grade dropdown is D18, applied to the rows before selectTableRows sees them, so
      the two filters compose with each other and with pagination.

  The grade filter is the one piece not inside the table state — it narrows rows rather
  than matching text — so its handler resets the page explicitly. That is the only place
  in this feature where the reset is written by hand, and it is written once.

  Two other things gone rather than moved:

    - **the sets query.** It existed to find `currentSet.id` for a `setId` prop
      SetViewTable never read. A whole network request served one dead prop.
    - **the second copy of the grade value.** FilterGradeDDL is controlled now, so the
      value the table filters by is the value the control shows.
*/

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from 'components/Breadcrumbs';
import FilterGradeDDL from 'components/FilterGradeDDL';
import FilterSearch from 'components/FilterSearch';
import GradedLanguagesChart from 'components/GradedLanguagesChart';
import PopulationBreakdown from 'components/PopulationBreakdown';
import SetHeader from 'components/SetHeader';
import SetTopCards from 'components/SetTopCards';
import SetViewTable, { SET_VIEW_DEFAULT_SORT, productsToCards } from 'components/Tables/SetViewTable';

import { useTableState } from 'components/DataTable';

import { useGetGamesQuery, useGetProductsWithCountsQuery } from 'services/pcgPublicV2';
import { useAllGameSets } from 'services/useAllGameSets';
import { formatCount } from 'lib';
import { cleanSetName, stripFalse } from 'lib/setName';
import { sanitizeURIPathSegment } from 'helpers/routing';

function SetView() {
  const params = useParams();
  const { gameCode = '', setName = '' } = params;
  const navigate = useNavigate();
  const { search, hash } = useLocation();

  const [filterGrade, setFilterGrade] = useState('');

  const { data: games = [] } = useGetGamesQuery();
  const gameInfo = games.find(g => g.gameCode.toLowerCase() === gameCode.toLowerCase());

  /*
    The game's whole set catalogue, for the header's rank and logo — and for the breakdown
    band, which scopes by setId: a URL set *name* only becomes an id through this lookup
    (§9.2). Until the set's row arrives the id is undefined and the band hides; it must
    never fall back to the unscoped request, which would print site-wide figures under
    this set's heading (§12.25).
  */
  const { sets, complete: setsComplete } = useAllGameSets(gameInfo?.id);
  const setId = sets.find(
    set => set.name.toLowerCase() === setName.toLowerCase()
  )?.id;

  /*
    An old link that still carries the `FALSE` debris (R17, D11) moves to the clean name —
    only once every set is in, since a set not loaded yet is not proof the name is gone,
    and only when the URL's own name is no set and the stripped one is. The path is built
    the way set links build it, and the query string and hash come along.
  */
  useEffect(() => {
    if (!setsComplete || setId !== undefined) {
      return;
    }
    const stripped = stripFalse(setName);
    if (stripped === setName) {
      return;
    }
    if (sets.some(set => set.name.toLowerCase() === stripped.toLowerCase())) {
      navigate(`/${gameCode}/${sanitizeURIPathSegment(stripped)}${search}${hash}`, { replace: true });
    }
  }, [setsComplete, setId, setName, sets, gameCode, search, hash, navigate]);

  /*
    The set's cards with their grade counts: the header's figures and the top-cards shelf.
    SetViewTable makes the same request, so RTK serves both from one fetch. `currentData`
    so set A's cards are never shown under set B's header while B loads.
  */
  const { currentData: products, isError: productsFailed } = useGetProductsWithCountsQuery(
    { gameCode, setName },
    { skip: !gameCode || !setName }
  );
  const cards = useMemo(
    () => (products ? productsToCards(products, gameCode) : undefined),
    [products, gameCode]
  );

  /* 25 was the old reducer's page size (limit: 25); DEFAULT_ROWS_PER_PAGE is 10. */
  const table = useTableState({ ...SET_VIEW_DEFAULT_SORT, rowsPerPage: 25 });
  const { setFilter, setPage } = table;

  /*
    Both filters reset the page (§6.5). setFilter does it itself — there is no setter that
    does one without the other — and the grade filter says so here because it is not part
    of that state.
  */
  const handleGradeChange = useCallback((value: string) => {
    setFilterGrade(value);
    setPage(0);
  }, [setPage]);

  /*
    A different set is a different list of cards, so both filters clear. FilterSearch
    holds the text a visitor is mid-way through typing, so it is keyed on the set to clear
    the visible box along with the state — the old `setSearch('')` here never did.
  */
  useEffect(() => {
    setFilter('');
    setFilterGrade('');
  }, [gameCode, setName, setFilter]);

  return (
    <>
      <Helmet title={setName ? cleanSetName(setName) : ''} />
      <div className="skin-stack flex flex-col gap-6">
        <Breadcrumbs
          crumbs={[
            {
              url: '/',
              text: 'Home',
            },
            {
              url: `/${gameCode}`,
              text: gameInfo?.name || '',
            },
            {
              url: '',
              /* Display name only; the url stays raw (pop-language-split D10). */
              text: setName ? cleanSetName(setName) : '',
            },
          ]}
        />

        {/* The set's h1, logo and figures (SetHeader). */}
        <SetHeader
          gameCode={gameCode}
          setName={setName}
          game={gameInfo}
          sets={sets}
          setsComplete={setsComplete}
          cards={cards}
        />

        {/* The set's graded languages (R16), nothing highlighted; hidden until setId resolves. */}
        <GradedLanguagesChart setId={setId} />

        {/* Skeletons while the cards load; gone on error, where the table below reports it. */}
        {productsFailed ? null : <SetTopCards gameCode={gameCode} setName={setName} cards={cards} />}

        {/* Scoped to this set, or hidden if the name could not be resolved to an id. */}
        <PopulationBreakdown scope="set" scopeId={setId} />

        {/*
          One toolbar row (SetViewTable lays it out): the "All Cards" heading and count on the
          left, the grade and search filters beside Show All Grades on the right.
        */}
        <SetViewTable
          gameCode={gameCode}
          setName={setName}
          table={table}
          filterGrade={filterGrade}
          heading={
            <>
              <h2 className="font-display text-lg font-semibold text-pcg-text sm:text-xl">
                All Cards
              </h2>
              {cards ? (
                <p className="mt-0.5 text-xs uppercase tracking-wide text-pcg-textMuted">
                  {formatCount(cards.length)} cards
                </p>
              ) : null}
            </>
          }
          controls={
            <>
              <FilterGradeDDL
                value={filterGrade}
                onChange={handleGradeChange}
                className="w-full sm:w-56"
              />
              <FilterSearch
                key={`${gameCode}/${setName}`}
                onChange={setFilter}
                label="Search Cards"
                className="w-full sm:w-72"
              />
            </>
          }
        />
      </div>
    </>
  );
}

export default SetView;
